import { useTranslation } from 'react-i18next'
import { Plus, Trash2 } from 'lucide-react'
import { Button, Card, Input } from '@/components/ui'
import { WEEKDAYS, type AvailabilityWindow, type Weekday, type WeeklyAvailability } from '@/types/visitor'

interface WeekdayAvailabilityEditorProps {
  availability: WeeklyAvailability
  onChange: (next: WeeklyAvailability) => void
  disabled?: boolean
}

/** Sensible default for a freshly-added window: a plain business-hours block. */
const DEFAULT_WINDOW: AvailabilityWindow = ['09:00', '17:00']

// Width of a time field. `Input`'s `className` targets the OUTER wrapper while
// the inner <input> is hardcoded `w-full`, so the wrapper is the only thing
// that can size one of these. This used to be `w-auto`, which shrank the
// wrapper to its intrinsic width and left the input filling 100% of almost
// nothing: the value collided with the browser's own clock picker and rendered
// as "09:0" with the icon on top of the last digit.
//
// Sized for the WIDEST case rather than the value we seed. A time input follows
// the BROWSER locale, not the app locale, so a Thai operator on an en-US
// browser gets "09:00 AM" here, not "09:00".
//
// `shrink-0` because the row wraps: without it a narrow viewport squeezes the
// fields back below their content width and reintroduces the same clipping.
const TIME_FIELD_WIDTH = 'w-32 shrink-0'

/**
 * A weekly GBP-style availability editor — per weekday, 0..n [open, close]
 * windows, closed days render empty (§8 Q-1 REFINED). Renders desktop rows
 * inside one Card and, per the T133-019 design, a separately-stacked Card
 * per weekday on mobile — the same row content either way, only the wrapper
 * changes (`renderRow`).
 */
export function WeekdayAvailabilityEditor({ availability, onChange, disabled }: WeekdayAvailabilityEditorProps) {
  const { t } = useTranslation('visitors')

  const windowsFor = (day: Weekday): AvailabilityWindow[] => availability[day] ?? []

  const setWindowsFor = (day: Weekday, windows: AvailabilityWindow[]) => {
    onChange({ ...availability, [day]: windows })
  }

  const addWindow = (day: Weekday) => {
    const existing = windowsFor(day)
    // Seed the new window right after the last one's close time so the
    // operator isn't stuck retyping an obviously-overlapping default.
    const last = existing[existing.length - 1]
    const next: AvailabilityWindow = last ? [last[1], DEFAULT_WINDOW[1]] : DEFAULT_WINDOW
    setWindowsFor(day, [...existing, next])
  }

  const removeWindow = (day: Weekday, index: number) => {
    setWindowsFor(day, windowsFor(day).filter((_, i) => i !== index))
  }

  const changeWindow = (day: Weekday, index: number, next: AvailabilityWindow) => {
    setWindowsFor(day, windowsFor(day).map((w, i) => (i === index ? next : w)))
  }

  const renderRow = (day: Weekday, variant: 'desktop' | 'mobile') => {
    const windows = windowsFor(day)
    return (
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-3 first:pt-0 last:pb-0">
        <span className="sm:w-28 shrink-0 font-medium text-sm">
          {t(`availability.weekday.${day}`)}
        </span>
        <div className="flex-1 min-w-0 flex flex-wrap items-center gap-2">
          {windows.length === 0 && (
            <span className="text-sm text-base-content/60">{t('availability.closed')}</span>
          )}
          {windows.map((w, idx) => (
            <div key={idx} className="flex items-center gap-1.5" data-testid={`${day}-window-${idx}-${variant}`}>
              <Input
                type="time"
                size="sm"
                className={TIME_FIELD_WIDTH}
                value={w[0]}
                disabled={disabled}
                aria-label={t('availability.window_from')}
                onChange={(e) => changeWindow(day, idx, [e.target.value, w[1]])}
              />
              <span className="text-base-content/40" aria-hidden="true">–</span>
              <Input
                type="time"
                size="sm"
                className={TIME_FIELD_WIDTH}
                value={w[1]}
                disabled={disabled}
                aria-label={t('availability.window_to')}
                onChange={(e) => changeWindow(day, idx, [w[0], e.target.value])}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                shape="circle"
                disabled={disabled}
                aria-label={t('availability.remove_window')}
                onClick={() => removeWindow(day, idx)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => addWindow(day)}
          >
            {windows.length === 0 ? t('availability.open_today') : t('availability.add_window')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Desktop — one Card, weekday rows divided by a hairline. */}
      <div className="hidden sm:block" data-testid="availability-desktop">
        <div className="divide-y divide-base-200">
          {WEEKDAYS.map((day) => (
            <div key={day}>{renderRow(day, 'desktop')}</div>
          ))}
        </div>
      </div>

      {/* Mobile — each weekday is its own stacked Card row (T133-019 design). */}
      <div className="sm:hidden space-y-3" data-testid="availability-mobile">
        {WEEKDAYS.map((day) => (
          <Card key={day} compact>
            {renderRow(day, 'mobile')}
          </Card>
        ))}
      </div>
    </div>
  )
}

// validateAvailability moved to ./availabilityValidation.ts —
// react-refresh/only-export-components forbids non-component exports here.
